import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Check, DollarSign, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Use relative URLs for same-origin requests (no CORS)
const API_URL = '';

// eslint-disable-next-line no-unused-vars
const MonetizationPlanModal = ({ user, onPlanSelected }) => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!selectedPlan) {
      toast({
        title: 'Please select a plan',
        description: 'Choose either the Subscription or Free plan to continue.',
        variant: 'destructive'
      });
      return;
    }

    setIsConfirming(true);

    try {
      const token = localStorage.getItem('access_token');
      
      // Map UI plan selection to backend plan names
      const planMapping = {
        'subscription': 'subscription_cashapp_venmo',
        'revshare': 'free_stripe_10percent'
      };
      
      const backendPlan = planMapping[selectedPlan];
      
      // Call select-plan endpoint
      const response = await axios.post(
        `${API_URL}/api/monetization/select-plan`,
        { plan: backendPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Check if user already has an active subscription
      if (response.data.already_subscribed) {
        toast({
          title: 'Already Subscribed!',
          description: response.data.message || 'You already have an active Pro Plan subscription.',
        });
        
        // Refresh user data
        const userResponse = await axios.get(
          `${API_URL}/api/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const updatedUser = userResponse.data;
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Notify parent component
        if (onPlanSelected) onPlanSelected(updatedUser);
        return;
      }

      // If subscription plan requires checkout, redirect to Stripe
      if (response.data.requires_checkout && response.data.checkout_url) {
        toast({
          title: 'Redirecting to checkout...',
          description: 'Complete payment to activate your subscription.',
        });
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
        return; // Don&apos;t close modal, user is being redirected
      }

      // For free plan or if subscription already active
      // Fetch updated user data from backend
      const userResponse = await axios.get(
        `${API_URL}/api/auth/me`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedUser = userResponse.data;
      
      // Update user in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));

      if (selectedPlan === 'subscription' && response.data.subscription_active) {
        toast({
          title: 'Subscription already active!',
          description: 'Your subscription is active. You can add your Cash App link now.'
        });
      } else if (selectedPlan === 'revshare') {
        toast({
          title: 'Free Plan selected!',
          description: 'Complete Stripe Connect setup to receive tips.'
        });
      }

      // Pass the updated user data back to parent
      onPlanSelected(updatedUser);
    } catch (error) {
      console.error('Error selecting plan:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to select plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FFCC00] to-[#FFD700] p-6 rounded-t-2xl">
          <h2 className="text-3xl font-bold text-black text-center mb-2">
            Choose Your Monetization Plan
          </h2>
          <p className="text-center text-gray-800 text-lg">
            Select how you want to receive tips from artists
          </p>
        </div>

        {/* Plans */}
        <div className="p-8 grid md:grid-cols-2 gap-6">
          {/* Pro Plan */}
          <Card
            className={`p-6 cursor-pointer transition-all border-2 hover:shadow-xl ${
              selectedPlan === 'subscription'
                ? 'border-[#FFCC00] bg-[#FFCC00]/5 shadow-lg scale-105'
                : 'border-gray-200 hover:border-[#FFCC00]/50'
            }`}
            onClick={() => setSelectedPlan('subscription')}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-black p-3 rounded-lg">
                <DollarSign className="h-8 w-8 text-[#FFCC00]" />
              </div>
              {selectedPlan === 'subscription' && (
                <div className="bg-[#FFCC00] p-2 rounded-full">
                  <Check className="h-5 w-5 text-black" />
                </div>
              )}
            </div>

            <h3 className="text-2xl font-bold text-black mb-2">Pro Plan</h3>
            <div className="text-4xl font-bold text-black mb-4">$10<span className="text-lg text-gray-600">/month</span></div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 font-semibold">Keep 100% of your tips</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>PRO badge</strong> on your profile</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700"><strong>Priority listing</strong> - appear first to artists</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Add your Cash App or Venmo link</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Artists tip you directly</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">No platform fee on tips</span>
              </li>
            </ul>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium">
                <strong>Example:</strong> If an artist tips you $20, you receive $20 (100%)
              </p>
            </div>

            {/* Plan switching notice */}
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700">
                <strong>Note:</strong> You can upgrade to this plan anytime. If you later want to switch back to the free plan, you can do so on the 1st of each month.
              </p>
            </div>

            {/* Payment notice */}
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <ExternalLink className="h-4 w-4 mr-2" />
              <span>Secure payment via Stripe</span>
            </div>
          </Card>

          {/* Free Plan (Rev-Share) */}
          <Card
            className={`p-6 cursor-pointer transition-all border-2 hover:shadow-xl ${
              selectedPlan === 'revshare'
                ? 'border-[#FFCC00] bg-[#FFCC00]/5 shadow-lg scale-105'
                : 'border-gray-200 hover:border-[#FFCC00]/50'
            }`}
            onClick={() => setSelectedPlan('revshare')}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-black p-3 rounded-lg">
                <CreditCard className="h-8 w-8 text-[#FFCC00]" />
              </div>
              {selectedPlan === 'revshare' && (
                <div className="bg-[#FFCC00] p-2 rounded-full">
                  <Check className="h-5 w-5 text-black" />
                </div>
              )}
            </div>

            <h3 className="text-2xl font-bold text-black mb-2">Free Plan</h3>
            <div className="text-4xl font-bold text-black mb-4">$0<span className="text-lg text-gray-600">/month</span></div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">No monthly fee</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Tips processed via Stripe</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 font-semibold">You receive 90% of tips</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Mic Check Millionaire takes 10%</span>
              </li>
            </ul>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800 font-medium">
                <strong>Example:</strong> If an artist tips you $20, you receive $18, Mic Check Millionaire receives $2
              </p>
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {selectedPlan === 'subscription' 
              ? 'You will be redirected to complete payment'
              : 'You can change your plan later in Settings'}
          </p>
          <Button
            onClick={handleConfirm}
            disabled={!selectedPlan || isConfirming}
            className="bg-black hover:bg-gray-800 text-white px-8 py-6 text-lg disabled:opacity-50"
            size="lg"
          >
            {isConfirming ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {selectedPlan === 'subscription' ? 'Redirecting...' : 'Confirming...'}
              </>
            ) : (
              selectedPlan === 'subscription' 
                ? 'Continue to Payment ($10/mo)'
                : 'Continue with Selected Plan'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MonetizationPlanModal;
